import React, { useState, useEffect, useRef } from 'react';
import { css, keyframes } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import { token } from '@atlaskit/tokens';
import LinkIcon from '@atlaskit/icon/core/link';

import { AnchorMacroPlaceholder } from '@confluence/fabric-extension-lib/entry-points/editor-extensions';

const i18n = defineMessages({
	anchorMacroLabel: {
		id: 'fabric-extension-handlers.inline-extension.valid-anchor-macro.icon.label',
		defaultMessage: 'Anchor link',
		description: `Label text for the icon shown in a valid anchor macro's body`,
	},
});

const highlightAnimation = keyframes({
	'0%': {
		boxShadow: 'unset',
		color: token('color.icon'),
	},
	'10%, 90%': {
		boxShadow: `0 0 0 3px ${token('color.border.focused')}`,
		color: token('color.icon.selected'),
	},
	'100%': {
		boxShadow: 'unset',
		color: token('color.icon'),
	},
});

const animationStyles = css({
	animation: `${highlightAnimation} 800ms linear`,
});

const macroWrapperStyles = css({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	backgroundColor: token('color.background.neutral'),
	width: '24px',
	height: '20px',
	overflow: 'hidden',
	borderRadius: token('radius.small'),
	boxSizing: 'border-box',
});

export const AnchorMacroRenderer = (props) => {
	const intl = useIntl();
	const [shouldPlayHighlightAnimation, setShouldPlayHighlightAnimation] = useState<boolean>(false);
	const [isMacroVisible, setIsMacroVisible] = useState<boolean>(false);
	const macroRef = useRef<HTMLDivElement>(null);

	const anchorName = props?.node?.parameters?.macroParams[''].value;

	let anchorValid = true;

	if (!anchorName || anchorName.includes(' ')) {
		anchorValid = false;
	}

	if (anchorValid) {
		try {
			decodeURIComponent(anchorName);
		} catch {
			anchorValid = false;
		}
	}

	useEffect(() => {
		setShouldPlayHighlightAnimation(anchorValid && window.location.hash === `#${anchorName}`);

		const handleOnHashChange = () => {
			setIsMacroVisible(false);
			setShouldPlayHighlightAnimation(anchorValid && window.location.hash === `#${anchorName}`);
		};

		// There is a case where a url hash change occurs but does not reload the page.
		// To account for this, so we can still trigger the animation as expected when
		// the related anchor is scrolled to, we need to listen to the event.
		window.addEventListener('hashchange', handleOnHashChange);

		return () => {
			window.removeEventListener('hashchange', handleOnHashChange);
		};
	}, [anchorName, anchorValid, setShouldPlayHighlightAnimation]);

	useEffect(() => {
		// This is to ensure that the animation is only triggered when the anchor is in view
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (!process.env.REACT_SSR) {
			/**
			 * IntersectionObserver doesn't work for SSR pages.
			 * Escaped using process.env.REACT_SSR above, per rule,
			 * and silenced eslint warning safely below.
			 */
			// eslint-disable-next-line
			const observer = new IntersectionObserver(
				([entry]) => {
					if (!isMacroVisible) {
						setIsMacroVisible(entry.isIntersecting);
					}
				},
				{ threshold: 1.0 },
			);

			if (macroRef?.current) {
				observer.observe(macroRef.current as Element);
			}

			// Remove the observer as soon as the component is unmounted
			return () => {
				observer.disconnect();
			};
		}
	}, [isMacroVisible, setIsMacroVisible, macroRef]);

	return (
		<div
			ref={macroRef}
			id={anchorValid ? anchorName : undefined}
			css={[macroWrapperStyles, shouldPlayHighlightAnimation && isMacroVisible && animationStyles]}
		>
			{anchorValid ? (
				<LinkIcon
					label={intl.formatMessage(i18n.anchorMacroLabel)}
					color={token('color.icon.subtle')}
				/>
			) : (
				<AnchorMacroPlaceholder />
			)}
		</div>
	);
};
